<template>
  <div class="section-container">
    <div class="section-header">
      <h2 class="section-title">{{ studentName }}的课程记录</h2>
      <div class="view-controls">
        <button 
          class="button button-outline"
          :class="{ active: currentView === 'monthly' }" 
          @click="setView('monthly')"
        >
          Monthly
        </button>
        <button 
          class="button button-outline"
          :class="{ active: currentView === 'weekly' }" 
          @click="setView('weekly')"
        >
          Weekly
        </button>
        <button 
          class="button button-outline"
          :class="{ active: currentView === 'daily' }" 
          @click="setView('daily')"
        >
          Daily
        </button>
      </div>
      <div class="date-navigation">
        <button class="button button-outline" @click="previousPeriod">←</button>
        <h3>{{ currentPeriodTitle }}</h3>
        <button class="button button-outline" @click="nextPeriod">→</button>
      </div>
    </div>

    <!-- Simplified Summary Section -->
    <div class="summary-section" v-if="!loading && !error">
      <div class="summary-card">
        <h3>出席情況</h3>
        <div class="summary-grid">
          <div class="summary-item">
            <span class="summary-label">总课程数</span>
            <span class="summary-value">{{ attendanceSummary.totalSessions }}</span>
          </div>
          <div class="summary-item absent-item">
            <span class="summary-label">缺席课程</span>
            <span class="summary-value">{{ attendanceSummary.absent }}</span>
            <span class="summary-percentage">({{ attendancePercentage(attendanceSummary.absent) }}%)</span>
          </div>
          <div class="summary-item makeup-item">
            <span class="summary-label">已补课程</span>
            <span class="summary-value">{{ attendanceSummary.makeupCompleted }}</span>
            <span class="summary-percentage" v-if="attendanceSummary.absent > 0">
              ({{ Math.round((attendanceSummary.makeupCompleted / attendanceSummary.absent) * 100) }}%)
            </span>
            <span class="summary-percentage" v-else>-</span>
          </div>
        </div>
      </div>
    </div>

    <div class="calendar-view">
      <MonthlyView 
        v-if="currentView === 'monthly'" 
        :sessions="filteredSessions" 
        :currentDate="currentDate"
        @session-click="handleSessionClick"
      />
      <WeeklyView 
        v-if="currentView === 'weekly'" 
        :sessions="filteredSessions" 
        :currentDate="currentDate"
        @session-click="handleSessionClick"
      />
      <DailyView 
        v-if="currentView === 'daily'" 
        :sessions="filteredSessions" 
        :currentDate="currentDate"
        @session-click="handleSessionClick"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { GET_CALENDAR_SESSIONS } from '../graphql/queries';
import { useRouter } from 'vue-router';
import MonthlyView from './calendar/MonthlyView.vue';
import WeeklyView from './calendar/WeeklyView.vue';
import DailyView from './calendar/DailyView.vue';

const router = useRouter();
const props = defineProps({
  student: {
    type: Object,
    required: true
  }
});

const currentView = ref('monthly');
const currentDate = ref(new Date());

const studentId = computed(() => props.student.student_id);
const studentName = computed(() => props.student?.name);

const { result, loading, error, refetch } = useQuery(
  GET_CALENDAR_SESSIONS, 
  () => ({
    startDate: getPeriodStart(),
    endDate: getPeriodEnd(),
    viewType: currentView.value.toUpperCase(),
  }),
  { fetchPolicy: 'network-only' }
);

// Compute attendance summary with proper ID comparison
const attendanceSummary = computed(() => {
  const summary = {
    totalSessions: 0,
    absent: 0,
    makeupCompleted: 0
  };

  if (!result.value?.calendarSessions) return summary;

  filteredSessions.value.forEach(session => {
    summary.totalSessions++;
    
    const isAbsent = session.absentStudents?.some(s => s.student_id === studentId.value);
    const makeupCompleted = session.completedMakeupClassStudents?.some(s => 
      s.student_id === studentId.value
    );
    
    if (makeupCompleted) {
      summary.makeupCompleted++;
    }
    
    if (isAbsent) {
      summary.absent++;
    }
  });

  return summary;
});

// Filter sessions with proper ID comparison
const filteredSessions = computed(() => {
  const allSessions = result.value?.calendarSessions || [];
  return allSessions.map(session => {
    const isEnrolled = session.students?.some(s => s.student_id === studentId.value);
    const isAbsent = session.absentStudents?.some(s => s.student_id === studentId.value);
    const makeupCompleted = session.completedMakeupClassStudents?.some(s => 
      s.student_id === studentId.value
    );
    
    return {
      ...session,
      students: isEnrolled ? [session.students.find(s => s.student_id === studentId.value)] : [],
      absentStudents: isAbsent ? [session.absentStudents.find(s => s.student_id === studentId.value)] : [],
      completedMakeupClassStudents: makeupCompleted 
        ? [session.completedMakeupClassStudents.find(s => s.student_id === studentId.value)] 
        : []
    };
  }).filter(session => 
    session.students.length > 0 ||
    session.absentStudents.length > 0 ||
    session.completedMakeupClassStudents.length > 0
  );
});

// Helper to calculate percentage
const attendancePercentage = (value) => {
  if (attendanceSummary.value.totalSessions === 0) return 0;
  return Math.round((value / attendanceSummary.value.totalSessions) * 100);
};

// Reuse all your existing date calculation methods from CalendarView.vue
function getPeriodStart() {
  const date = new Date(currentDate.value);
  if (currentView.value === 'monthly') {
    date.setDate(1);
    return normalizeDateStart(date);
  } else if (currentView.value === 'weekly') {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    date.setDate(diff);
    return normalizeDateStart(date);
  } else {
    return normalizeDateStart(date);
  }
}

function getPeriodEnd() {
  const date = new Date(currentDate.value);
  if (currentView.value === 'monthly') {
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return normalizeDateEnd(date);
  } else if (currentView.value === 'weekly') {
    const start = getPeriodStart();
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return normalizeDateEnd(end);
  } else {
    return normalizeDateEnd(date);
  }
}

function normalizeDateStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function normalizeDateEnd(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

const currentPeriodTitle = computed(() => {
  if (currentView.value === 'monthly') {
    return currentDate.value.toLocaleDateString('default', { month: 'long', year: 'numeric' });
  } else if (currentView.value === 'weekly') {
    const start = getPeriodStart();
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  } else {
    return currentDate.value.toLocaleDateString();
  }
});

function setView(view) {
  currentView.value = view;
}

function previousPeriod() {
  const date = new Date(currentDate.value);
  if (currentView.value === 'monthly') {
    date.setMonth(date.getMonth() - 1);
  } else if (currentView.value === 'weekly') {
    date.setDate(date.getDate() - 7);
  } else {
    date.setDate(date.getDate() - 1);
  }
  currentDate.value = date;
}

function nextPeriod() {
  const date = new Date(currentDate.value);
  if (currentView.value === 'monthly') {
    date.setMonth(date.getMonth() + 1);
  } else if (currentView.value === 'weekly') {
    date.setDate(date.getDate() + 7);
  } else {
    date.setDate(date.getDate() + 1);
  }
  currentDate.value = date;
}

function handleSessionClick(session) {
  // Handle session click to navigate to attendance page
  router.push(`/session/${session.session_id}/attendance`);
}

watch([currentView, currentDate], () => {
  refetch({
    startDate: getPeriodStart(),
    endDate: getPeriodEnd(),
    viewType: currentView.value.toUpperCase()
  });
});
</script>


<style scoped>
/* Calendar Container */
.section-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
}

.section-title {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-dark);
  flex: 1 1 100%;
}

/* View Controls */
.view-controls {
  display: flex;
  gap: 0.5rem;
  margin-right: auto;
}

.date-navigation {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.date-navigation h3 {
  margin: 0;
  min-width: 200px;
  text-align: center;
  color: var(--text-medium);
  font-weight: 500;
}

/* Summary Section */
.summary-section {
  margin: 1.5rem 0;
}

.summary-card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color);
}

.summary-card h3 {
  margin-top: 0;
  margin-bottom: 1.25rem;
  color: var(--text-dark);
  font-size: 1.1rem;
  font-weight: 600;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0.5rem;
  background: var(--light-bg);
  border-radius: 6px;
  transition: transform 0.2s ease;
}

.summary-item:hover {
  transform: translateY(-2px);
}

.summary-label {
  font-size: 0.9rem;
  color: var(--text-medium);
  margin-bottom: 0.5rem;
}

.summary-value {
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.summary-percentage {
  font-size: 0.85rem;
  color: var(--text-light);
}

/* Special item styles */
.absent-item .summary-value {
  color: var(--danger-color);
}

.makeup-item .summary-value {
  color: var(--secondary-color);
}

/* Calendar View */
.calendar-view {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  margin-top: 1.5rem;
}

/* Active state for view buttons */
.button-outline.active {
  background-color: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .view-controls {
    margin-right: 0;
    margin-bottom: 1rem;
  }
  
  .date-navigation {
    width: 100%;
    justify-content: space-between;
  }
  
  .summary-grid {
    grid-template-columns: 1fr;
  }
  
  .summary-item {
    padding: 1.25rem;
  }
}
</style>